<template>
  <v-row justify="center">
    <v-dialog v-model="show" persistent max-width="50%">
      <custom-modal
        title="Edit Room Type"
        @onSubmit="submit"
        @onClose="onClose()"
        :isLoading="isLoading"
      >
        <ValidationObserver ref="observer" v-slot="{ validate, reset }">
          <v-row>
            <v-col cols="6">
              <v-row>
                <v-col cols="12">
                  <ValidationProvider
                    v-slot="{ errors }"
                    name="Name"
                    rules="required|no_spaces_only"
                    vid="name"
                  >
                    <v-text-field
                      outlined
                      v-model="roomType.name"
                      :error-messages="errors"
                      dense
                      hide-details="auto"
                      label="Name"
                      @blur="roomType.name = roomType.name.trim()"
                    ></v-text-field>
                  </ValidationProvider>
                </v-col>
              </v-row>

              <v-row>
                <v-col cols="6">
                  <ValidationProvider
                    name="code"
                    rules="required|no_spaces_only"
                    v-slot="{ errors }"
                    vid="code"
                  >
                    <v-text-field
                      v-model="roomType.code"
                      :error-messages="errors"
                      outlined
                      dense
                      hide-details="auto"
                      label="Code"
                      required
                      @blur="roomType.code = roomType.code.trim()"
                    ></v-text-field>
                  </ValidationProvider>
                </v-col>
                <v-col cols="6">
                  <ColorPicker v-model="roomType.color"></ColorPicker>
                </v-col>
              </v-row>

              <v-row>
                <v-col cols="12">
                  <v-row>
                    <v-col cols="6">
                      <ValidationProvider
                        v-slot="{ errors }"
                        name="Base Adult"
                        rules="required|min_value:1"
                        vid="base_adult"
                      >
                        <v-text-field
                          v-model="roomType.base_adult"
                          :error-messages="errors"
                          label="Base Adult"
                          dense
                          outlined
                          type="number"
                          min="1"
                        ></v-text-field>
                      </ValidationProvider>
                    </v-col>

                    <v-col cols="6">
                      <ValidationProvider
                        v-slot="{ errors }"
                        name="Maximum Adult"
                        rules="required|min_value:1|greater_than_or_equal:@base_adult"
                      >
                        <v-text-field
                          v-model="roomType.max_adult"
                          :error-messages="errors"
                          label="Maximum Adult"
                          dense
                          outlined
                          type="number"
                          min="1"
                        ></v-text-field>
                      </ValidationProvider>
                    </v-col>
                  </v-row>

                  <v-row>
                    <v-col cols="6">
                      <ValidationProvider
                        v-slot="{ errors }"
                        name="Base Child"
                        rules="required|min_value:1"
                        vid="base_child"
                      >
                        <v-text-field
                          v-model="roomType.base_child"
                          :error-messages="errors"
                          label="Base Child"
                          dense
                          outlined
                          type="number"
                          min="1"
                        ></v-text-field>
                      </ValidationProvider>
                    </v-col>
                    
                    <v-col cols="6">
                      <ValidationProvider
                        v-slot="{ errors }"
                        name="Maximum Child"
                        rules="required|min_value:1|greater_than_or_equal:@base_child"
                      >
                        <v-text-field
                          v-model="roomType.max_child"
                          :error-messages="errors"
                          label="Maximum Child"
                          dense
                          outlined
                          type="number"
                          min="1"
                        ></v-text-field>
                      </ValidationProvider>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12">
                  <v-textarea
                    rows="3"
                    outlined
                    v-model="roomType.note"
                    label="Note"
                  ></v-textarea>
                </v-col>
              </v-row>
            </v-col>
            <v-col cols="6">
              <v-row>
                <v-col cols="12" class="pt-0">
                  <v-card-title class="px-0 pt-0">Amenities</v-card-title>
                  <v-list shaped class="amenties-list">
                    <v-list-item-group v-model="roomType.amenities" multiple>
                      <template v-for="(item, i) in amenities">
                        <v-divider
                          v-if="!item"
                          :key="`divider-${i}`"
                        ></v-divider>

                        <v-list-item
                          v-else
                          :key="`item-${i}`"
                          :value="item"
                          active-class="deep-purple--text text--accent-4"
                        >
                          <template v-slot:default="{ active }">
                            <v-list-item-content>
                              <v-list-item-title
                                v-text="item.name"
                              ></v-list-item-title>
                            </v-list-item-content>

                            <v-list-item-action>
                              <v-checkbox
                                :input-value="item.checked"
                                color="deep-purple accent-4"
                              ></v-checkbox>
                            </v-list-item-action>
                          </template>
                        </v-list-item>
                      </template>
                    </v-list-item-group>
                  </v-list>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </ValidationObserver>
      </custom-modal>
    </v-dialog>
  </v-row>
</template>

<script>
import { Payment } from "@/models/Payment";
import datePickComponent from "@/components/base/DateComponent";
import FileUpload from "@/components/base/FileUpload";
import { required, numeric, min_value } from "vee-validate/dist/rules";
import axios from "@/plugins/axios";

import { extend, ValidationObserver, ValidationProvider } from "vee-validate";
import { RoomType } from "../../../models/RoomType";
import ColorPicker from "../../../components/base/ColorPicker.vue";
extend("required", {
  ...required,
  message: "{_field_} is required",
});
extend("no_spaces_only", {
  validate: (value) => {
    return value && value.trim().length > 0;
  },
  message: "{_field_} cannot be empty or spaces only",
});
extend("numeric", {
  ...numeric,
  message: "input must be numeric",
});
extend("min_value", {
  ...min_value,
  message: "{_field_} must be at least {min}",
});
extend("max", {
  ...numeric,
  message: "amount must be less than balance",
});
extend("greater_than_or_equal", {
  params: ["target"],
  validate(value, { target }) {
    return Number(value) >= Number(target);
  },
  message: "{_field_} must be greater than or equal to {target}",
});

export default {
  data: () => ({
    roomType: new RoomType(),
    dialog: false,
    show: false,
    selectedFiles: [],
    amenities: [
      { id: "1", name: "Cash" },
      { id: "2", name: "Cross" },
      { id: "1", name: "Cash" },
      { id: "2", name: "Cross" },
      { id: "1", name: "Cash" },
      { id: "2", name: "Cross" },
      { id: "1", name: "Cash" },
      { id: "2", name: "Cross" },
      { id: "1", name: "Cash" },
      { id: "2", name: "Cross" },
      { id: "1", name: "Cash" },
      { id: "2", name: "Cross" },
      { id: "1", name: "Cash" },
      { id: "2", name: "Cross" },
      { id: "1", name: "Cash" },
      { id: "2", name: "Cross" },
      { id: "1", name: "Cash" },
      { id: "2", name: "Cross" },
      { id: "1", name: "Cash" },
      { id: "2", name: "Cross" },
    ],
  }),

  name: "AddBatch",
  updated: function() {},
  props: {
    creditAccounts: {
      type: Array,
      default: [],
    },
  },
  components: {
    ValidationProvider,
    ValidationObserver,
    datePickComponent,
    FileUpload,
  },
  computed: {},
  methods: {
    async openModal(id) {
      this.show = true;
      await this.getRoomType(id);
      await this.getAmenties();
    },
    async submit() {
      console.log(
        "================================================================"
      );
      console.log(
        "================================================================"
      );
      console.log(
        "================================================================"
      );
      console.log(this.roomType);
      const isValid = await this.$refs.observer.validate();
      if (isValid) {
        this.updateRoomType();
      }
    },
    async getAmenties() {
      let self = this;
      axios
        .get(`hms/amenities`)
        .then((res) => {
          this.amenities = res.data.data;
          this.amenities.forEach((amenity) => {
            if (self.roomType.amenities.find((a) => a.id == amenity.id)) {
              amenity["checked"] = true;
            } else {
              amenity["checked"] = false;
            }
          });
        })
        .catch((err) => {});
    },
    async getRoomType(id) {
      axios
        .get(`hms/room-types/${id}`)
        .then((res) => {
          this.roomType = new RoomType().toViewModel(res.data);
        })
        .catch((err) => {});
    },
    updateRoomType() {
      axios
        .patch(
          `hms/room-types/${this.roomType.id}`,
          this.roomType.toRequestModel()
        )
        .then((res) => {
          this.$emit("afterSave", true);
          this.show = false;
          this.roomType = new RoomType();
          this.$toast.success("Room type updated successfully");
        })
        .catch((err) => {
          console.log(err);
          this.$refs.observer.setErrors(err.response.data.error);
        });
    },
    onClose() {
      this.show = false;
      this.roomType = new RoomType();
    },
  },
  created() {
    this.getAmenties();
  },
};
</script>

<style scoped>
.amenties-list {
  overflow-y: scroll;
  height: 350px;
}
</style>
